import { useCallback } from "react";
import { useDispatch } from "react-redux";
import { useNavigate } from "react-router";
import { useNotificationContext } from "@entrilia/shared/contexts/NotificationContext";
import { logError } from "@entrilia/shared/logging";
import biApi from "../../../../../../api/biApi";
import type { ReportTemplate } from "../../../../../../api/types/svTypes";
import { getEditReportTemplateUrl } from "../../../report-lists/reports/utilities/editReportUrl";
import { useBiClientContext } from "../../contexts/ClientContext";
import type { AppDispatch } from "../../store/store";
import { setEditReport } from "../../store/thunks/currentReportThunks";
import useReportTypeChange from "../builder/common/hooks/useReportTypeChange";
import Visualization from "../common/visualization/Visualization";

interface Props {
  template: ReportTemplate;
}

const TemplateTypeSelector = ({ template }: Props) => {
  const { clientInfo } = useBiClientContext();
  const { sendNotification, sendNotificationError } = useNotificationContext();
  const { onReportTypeChanged } = useReportTypeChange();
  const dispatch: AppDispatch = useDispatch();
  const navigate = useNavigate();

  const update = useCallback(async (template: ReportTemplate): Promise<ReportTemplate | undefined> => {
    try {
      const response = await biApi.updateReportTemplate({ template });
      if (response.success) {
        sendNotification("The template was successfully saved.");
        dispatch(setEditReport(response.data));

        return response.data;
      }
      sendNotificationError("An error occurred while saving the template.");

      return undefined;
    } catch (error) {
      sendNotificationError("An error occurred while saving the template.");
      logError(error, "[TemplateTypeSelector] update");

      return undefined;
    }
    // eslint-disable-next-line @eslint-react/exhaustive-deps
  }, []);

  const handleSaveChangesSaved = useCallback(
    (template: ReportTemplate) => {
      navigate(
        getEditReportTemplateUrl(template.organization, template.dataSource || clientInfo.clientCode, template.reportId)
      );
    },
    [clientInfo.clientCode, navigate]
  );

  return (
    <Visualization
      report={template}
      onSelect={onReportTypeChanged}
      update={update}
      onSaveChangesSaved={handleSaveChangesSaved}
    >
      <Visualization.Label />
      <Visualization.ReportTypesContainer />
      <Visualization.UnsavedChangesPopover reportKind="template" />
      <Visualization.SaveTemplateDialog />
    </Visualization>
  );
};

export default TemplateTypeSelector;
